import * as React from 'react';
import ContractFilter from './ContractFilter';
import TreeIdFilter from "./TreeIdFilter";
import DateRangeFilter from './DateFilter';
import TreeMaps from "../TreeMaps";
// Validator filter: feature to be implemented later (see the commented block below)
// import * as UserRepository from "../../../Repository/UserRepository";
// import ValidatorFilter from './ValidatorFilter';
import { Collapse } from "antd/es";
import moment from 'moment';
import { ConnectionState, GlobalContext } from "../../../Contexts/GlobalContext";
import { Row, Col, Form, Button, message, Space, Checkbox } from 'antd';
import { isMobileDevice, isDesktopLayout } from '../../../Helper/DeviceHelper';
import { lockPageScroll, unlockPageScroll } from '../../../Helper/ScrollLock';
import { hasTreesListCopy, isNativeApp } from '../../../Dependency/NativeAppMemory';
import { describeError, isOfflineError } from '../../../Helper/ErrorHelper';
import { NO_NETWORK_CONNECTIVITY } from '../../../Helper/BannerMessages';

const { Panel } = Collapse;

// Air on the right of the Dates field (last element in the Filters strip for now)
const FILTERS_LAST_PADDING_RIGHT = 150;

interface FiltersProps {
  filter: any;
  mapRef: any;
  getTrees: Function;
  updateState: Function;
  /** Days (local `YYYY-MM-DD`) with at least one tree matching the applied filters (see MapPage) */
  plantedDates?: string[];
  /** True while those days are being read (the calendar shows a spinner meanwhile) */
  plantedDatesLoading?: boolean;
  /** Trees planted matching the applied filter, shown in the panel header */
  filteredTreeCount?: number;
  /** Recomputes `plantedDates` for a filter that has just been applied */
  refreshPlantedDates?: Function;
  loading?: boolean;
  /** Panel opened or closed: the map starts under it while it is open (see MapPage) */
  onToggle?: Function;
}

export interface MapPageFormFilter {
  isLoading: boolean;
  onChange?: Function;
  value?: any;
}

export default function Filters(props: FiltersProps) {
  const { filter, mapRef, loading, plantedDates, plantedDatesLoading, filteredTreeCount, refreshPlantedDates } = props;
  const { onlineStatus } = React.useContext(GlobalContext);
  const [isLoading, setIsLoading] = React.useState(false);

  // A tree, a contract or a date range on the applied filter: the panel then displays "Filtered ( N trees"
  const hasAppliedFilter = !!(filter?.treeId || filter?.contractId || filter?.validatorId || filter?.dateStart || filter?.dateEnd);

  const [activeKey, setActiveKey] = React.useState<string | string[]>([]);
  const [form] = Form.useForm();
  // One answer for the whole render: the app and a mobile browser must agree, see DeviceHelper.
  const handheld = isMobileDevice();

  // A filter that returns no tree re-enables Auto-Zoom, so the camera can come back to the default view
  React.useEffect(() => {
    if (filteredTreeCount === 0) {
      form.setFieldsValue({ zoomOut: true });
    }
  }, [filteredTreeCount]);

  const onCollapseChange = (key: string | string[]) => {
    setActiveKey(key);
  };

  // Reported from the state and not from the click: the panel also closes itself on Reset and on applying
  React.useEffect(() => {
    props.onToggle?.(activeKey.length > 0);
  }, [activeKey]);

  // The page holds still while the Filters are being filled in, on the same 992px breakpoint the layout uses
  React.useEffect(() => {
    if (isDesktopLayout() || activeKey.length === 0) return;
    lockPageScroll();
    return unlockPageScroll;
  }, [activeKey]);

  const onReset = () => {
    const zoomOut = form.getFieldValue('zoomOut');
    form.resetFields();
    form.setFieldsValue({ zoomOut });
    onFinish({
      treeId: null,
      contractId: null,
      validatorId: null,
      dateStart: null,
      dateEnd: null,
      zoomOut,
    });
    if (isMobileDevice()) {
      setActiveKey([]);
    }
  }

  const onFinish = async (values: any) => {
    try {
      const mapObj = (mapRef as React.MutableRefObject<TreeMaps>).current;

      // Applying a filter closes the panel on both form factors: the result on the map is what matters
      setActiveKey([]);
      if (isMobileDevice()) {
        // Small delay to allow the drawer/collapse animation to begin
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setIsLoading(true);
      // The trees list copy only exists in the native app: a browser has nothing to fall back on
      const fromOfflineCopy = onlineStatus === ConnectionState.offline && isNativeApp();
      if (fromOfflineCopy) {
        // The filter still runs, on the trees the last load left in the trees list copy
        if (!(await hasTreesListCopy())) {
          message.info(NO_NETWORK_CONNECTIVITY);
          setIsLoading(false);
          return;
        }
      }

      // The map's "Loading data..." starts on the Filter click
      mapObj?.setLoadingState(true);

      // We use a small timeout to ensure the UI has updated (closing the menu) 
      // before we trigger the intensive map filtering operations
      setTimeout(async () => {
        try {
          // The form keeps the period as a `dates` pair, the filter as unix seconds
          const { dates, ...filterValues } = values;
          if (Array.isArray(dates)) {
            filterValues.dateStart = dates[0] ? Math.floor(dates[0].valueOf() / 1000) : null;
            filterValues.dateEnd = dates[1] ? Math.floor(dates[1].valueOf() / 1000) : null;
          }
          // The trees list copy holds all user' contracts so all filters work offline
          // possible failures are all reported during trees list copy (see `MapPage`)
          // 1. Update trees list & counter (left-side) first
          await props.getTrees(undefined, filterValues, { currentPage: 1 });
          // 2. Refresh the planting days in parallel: the calendar tracks it with its own spinner, so
          //    the map's "Loading data..." no longer waits for them
          refreshPlantedDates?.(filterValues);
          // 3. Update trees on the map (right-side) last
          await mapObj?.applyFilter(filterValues);
          setIsLoading(false);
        } catch (err) {
          console.error(err);
          // Offline is said where it is raised: the panel only puts its own state back
          if (!isOfflineError(err)) {
            message.error(describeError(err));
          }
          // The overlay went up on the click: a failed step must not leave it
          mapObj?.setLoadingState(false);
          setIsLoading(false);
        }
      }, handheld ? 300 : 0);

    } catch (err) {
      console.error(err);
      if (!isOfflineError(err)) {
        message.error(describeError(err));
      }
      setIsLoading(false);
    }
  };

  return (
    <Collapse
      ghost
      className="map-filters"
      activeKey={activeKey}
      onChange={onCollapseChange}
    >
      {/* The trees count comes with "Filtered" only: the default header is just "Filters" */}
      {/* The second space is a non-breaking one: HTML would collapse two plain spaces into one */}
      <Panel
        key="1"
        header={
          <span style={{ fontSize: '16px' }}>
            {hasAppliedFilter
              ? `Filtered${filteredTreeCount === undefined ? '' : `\u00A0 ( ${filteredTreeCount} ${filteredTreeCount <= 1 ? 'tree' : 'trees'} )`}`
              : 'Filters'}
          </span>
        }
      >
        <Form
          form={form}
          onFinish={onFinish}
          name="mapFilters"
          className="ant-advanced-search-form"
          initialValues={{
            treeId: filter?.treeId ?? null,
            contractId: filter?.contractId ?? null,
            validatorId: filter?.validatorId ?? null,
            // The filters in force, `Save Offline` included: its dates travel as unix seconds
            dates: [
              filter?.dateStart ? moment.unix(filter.dateStart) : null,
              filter?.dateEnd ? moment.unix(filter.dateEnd) : null,
            ],
            zoomOut: filter?.zoomOut ?? true,
          }}
        >
          <div className="filter-form-container">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={6}>
                <Form.Item
                  name={`treeId`}
                  label={`Tree ID`}
                  className="filter-form-item"
                  rules={[
                    {
                      validator: (_: any, value: any) => {
                        if (value && isNaN(parseInt(value))) {
                          return Promise.reject();
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <TreeIdFilter isLoading={isLoading} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name={`contractId`}
                  label={`Contract`}
                  className="filter-form-item"
                  rules={[{ required: false }]}
                >
                  <ContractFilter isLoading={isLoading} />
                </Form.Item>
              </Col>
              <Col xs={24} md={7}>
                <Form.Item
                  name={`dates`}
                  label={`Dates`}
                  className="filter-form-item date-filter"
                  style={{ paddingRight: FILTERS_LAST_PADDING_RIGHT }}
                  rules={[{ required: false }]}
                >
                  <DateRangeFilter isLoading={isLoading} plantedDates={plantedDates} plantedDatesLoading={plantedDatesLoading} inputReadOnly={handheld} />
                </Form.Item>
              </Col>
              {/* Validator filter: feature to be implemented later
              {onlineStatus === ConnectionState.online && user && UserRepository.userHasRole(user.roles, UserRepository.UserRoles.Admin.name) && (
                <Col xs={24} md={5}>
                  <Form.Item
                    name={`validatorId`}
                    label={`Validator`}
                    className="filter-form-item"
                    rules={[{ required: false }]}
                  >
                    <ValidatorFilter isLoading={isLoading} />
                  </Form.Item>
                </Col>
              )}
              */}
            </Row>
            <div className="filter-actions">
              <Space size={handheld ? 'middle' : 48}>
                <Form.Item
                  name={`zoomOut`}
                  valuePropName={"checked"}
                  className="zoom-out-checkbox"
                >
                  <Checkbox indeterminate={false}>
                    Auto-Zoom
                  </Checkbox>
                </Form.Item>
                <Form.Item className="form-action-item">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    disabled={loading}
                    className="filter-button"
                  >
                    Filter
                  </Button>
                </Form.Item>
                <Form.Item className="form-action-item">
                  <Button
                    htmlType="button"
                    onClick={onReset}
                    disabled={isLoading || loading}
                    className="reset-button"
                  >
                    Reset
                  </Button>
                </Form.Item>
              </Space>
            </div>
          </div>
        </Form>
      </Panel>
    </Collapse>
  );
}